import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Modal, Form, Input, DatePicker, notification, Select } from 'antd';
import { useReleasesLogic } from '../useReleasesLogic';
import { version, title } from '../../validation/regex';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';
import { useApplicationsLogic } from '../useApplicationsLogic';

const CreateReleaseModal = ({ isVisible, onSaveCallback, onCloseCallback }) => {
	dayjs.extend(utc);

	const releasesLogic = useReleasesLogic();
	const applicationsLogic = useApplicationsLogic();

	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);
	const [areFormErrors, setAreFormErrors] = useState(true);
	const [applications, setApplications] = useState(null);

	const { t, getDateFormat, getAntLocale } = useI18nLocalization();
	const { Option } = Select;

	useEffect(() => {
		if (!applications) {
			getApplications();
		}
	}, [JSON.stringify(applications)]);

	const getApplications = async () => {
		const apps = await applicationsLogic.getApplications();
		setApplications(apps);
	};

	//-- Gather the form data
	const getNewRelease = (formValues) => {
		const newReleaseObj = {
			applicationId: formValues.applicationId,
			version: formValues.version,
			releaseDateTimeUtc: formValues.releaseDate.utc().format(),
			title: formValues.title,
			notes: formValues.notes
		};
		return newReleaseObj;
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const newRelease = getNewRelease(formValues);
		const successful = await releasesLogic.createRelease(newRelease);
		if (successful) {
			notification['success']({
				message: t(`Release created!`)
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t('CreateReleaseError'),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const clearForm = () => {
		form.resetFields();
	};

	return (
		<>
			<Modal
				title={t('Create a new release')}
				visible={isVisible}
				okText={t('Save')}
				onOk={() => {
					//-- Will trigger the form's onFinish event
					form.submit();
				}}
				okButtonProps={{ disabled: areFormErrors }}
				confirmLoading={isSaving}
				onCancel={handleOnCancel}
				cancelText={t('Cancel')}
			>
				<Form
					form={form}
					onFieldsChange={() => {
						const areErrors = form.getFieldsError().some((fe) => fe.errors.length > 0);
						setAreFormErrors(areErrors);
					}}
					layout='vertical'
					requiredMark
					scrollToFirstError
					onFinish={handleOnSave}
				>
					<Form.Item label={t('Application')} name='applicationId' required>
						<Select id='applicationId' placeholder={t('application')}>
							{_.map(applications, (app) => {
								return (
									<Option key={app.id} value={app.id}>
										{app.name}
									</Option>
								);
							})}
						</Select>
					</Form.Item>
					<Form.Item
						label={t('Version')}
						name='version'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('Version is required')));
									}

									if (!value.match(version) || value.length > 14) {
										return Promise.reject(new Error(`1-14 ${t('chars (inclusive)')}; M -- M.m -- M.m.b`));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<Input id='releaseVersion' placeholder={t('version')} />
					</Form.Item>
					<Form.Item
						label={t('Release Date')}
						name='releaseDate'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('Release Date is required')));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<DatePicker locale={getAntLocale().DatePicker} format={getDateFormat()} />
					</Form.Item>
					<Form.Item
						label={t('Title')}
						name='title'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('Title is required')));
									}

									if (!value.match(title)) {
										return Promise.reject(new Error(`1-50 ${t('alpha and some special chars (inclusive)')}`));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<Input placeholder={t('title')} />
					</Form.Item>
					<Form.Item
						label={t('Notes')}
						name='notes'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('Notes are required')));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<Input.TextArea rows={10} placeholder={t('notes')} style={{ whiteSpace: 'pre-wrap' }} />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

CreateReleaseModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	onCloseCallback: PropTypes.func.isRequired
};

export default CreateReleaseModal;
